import {
  Step3PrimeBatteryValues,
  Step3PrimeFuelValues,
  Step3PrimeKivaValues,
  Step3PrimeOtherValues,
} from "../types";

export const batteriesToProducts: {
  [key in keyof Step3PrimeBatteryValues]: {
    productName: string;
    amountWeightPercent: number;
  }[];
} = {
  chlLeadAcid: [
    {
      productName: "Sulfuric Acid",
      amountWeightPercent: 0.05,
    },
    {
      productName: "Lead",
      amountWeightPercent: 0.7,
    },
  ],
  otherLeadAcid: [
    {
      productName: "Sulfuric Acid",
      amountWeightPercent: 0.3,
    },
    {
      productName: "Lead",
      amountWeightPercent: 0.7,
    },
  ],
  lithiumIon: [
    {
      productName: "Lithium",
      amountWeightPercent: 0.75,
    },
  ],
};

export const kivaToProducts: {
  [key in keyof Step3PrimeKivaValues]: {
    productName: string;
    amountPounds: number;
  }[];
} = {
  rKiva: [
    {
      productName: "Sulfuric Acid",
      amountPounds: 28.8,
    },
    {
      productName: "Lead",
      amountPounds: 104.8,
    },
  ],
  xKiva: [
    {
      productName: "Lithium",
      amountPounds: 0.335103,
    },
  ],
  gSKiva: [
    {
      productName: "Lithium",
      amountPounds: 0.837757,
    },
  ],
  hPPtPlKiva: [
    {
      productName: "Lithium",
      amountPounds: 0.670205,
    },
  ],
};

// Each one of these points to one product in the product catalog
export const fuelProducts: {
  [key in keyof Step3PrimeFuelValues]: string;
} = {
  diesel: "Diesel",
  hydrogen: "Hydrogen",
  propane: "Propane",
};

// Each one of these points one product in the product catalog
export const otherProducts: {
  [key in keyof Step3PrimeOtherValues]: string;
} = {
  antifreeze: "Antifreeze",
  dryIce: "Dry Ice",
  handSanitizer: "Hand Sanitizer",
  refrigerant: "Refrigerant",
  rockSalt: "Rock Salt",
};

/*
 * I can already hear you asking - Kyle, what fresh nonsense is this?
 * And I know: that's a lot of ellipses down there. Basically, to minimize the amount of
 * code that needs to be changed, we're doing a little flattening out of the constants
 * above to just retrieve the products that they name. Then, to avoid having the same product
 * name multiple times, we wrap all of those flattened arrays into a set and expand that back
 * to an array so that we end up with an array containing unique product names contained in the
 * objects above.
 *
 * TL/DR: this is just a collection of product names that step3Prime might make use of.
 */
export const step3PrimeBatteryAndKivaProducts: string[] = [
  ...new Set([
    /*
     * This is the most convoluted expansion because of the double flat, but basically, we're just navigating through
     * to the productName keys of each battery and kiva type
     */
    ...Object.keys(batteriesToProducts)
      .map((k) =>
        batteriesToProducts[k as keyof typeof batteriesToProducts].flatMap((i) => i.productName)
      )
      .flat(),
    ...Object.keys(kivaToProducts)
      .map((k) => kivaToProducts[k as keyof typeof kivaToProducts].flatMap((i) => i.productName))
      .flat(),
  ]),
];

export const step3PrimeProducts: string[] = [
  ...new Set([
    /*
     * This is the most convoluted expansion because of the double flat, but basically, we're just navigating through
     * to the productName keys of each battery and kiva type
     */
    ...Object.keys(batteriesToProducts)
      .map((k) =>
        batteriesToProducts[k as keyof typeof batteriesToProducts].flatMap((i) => i.productName)
      )
      .flat(),
    ...Object.keys(kivaToProducts)
      .map((k) => kivaToProducts[k as keyof typeof kivaToProducts].flatMap((i) => i.productName))
      .flat(),
    ...Object.keys(fuelProducts).map((k) => fuelProducts[k as keyof typeof fuelProducts]),
    ...Object.keys(otherProducts).map((k) => otherProducts[k as keyof typeof otherProducts]),
  ]),
];

export const step3Prime1To1Products: string[] = step3PrimeProducts.filter(
  (i) => !step3PrimeBatteryAndKivaProducts.includes(i)
);

/*
 * And why do we not just do a key breakdown here like the above? Because we can't control the order.
 */
export const batteryProductDisplayOrder: (keyof Step3PrimeBatteryValues)[] = [
  "chlLeadAcid",
  "otherLeadAcid",
  "lithiumIon",
];
export const kivaProductDisplayOrder: (keyof Step3PrimeKivaValues)[] = [
  "xKiva",
  "gSKiva",
  "hPPtPlKiva",
  "rKiva",
];
export const fuelProductDisplayOrder: (keyof Step3PrimeFuelValues)[] = [
  "diesel",
  "hydrogen",
  "propane",
];
export const otherProductDisplayOrder: (keyof Step3PrimeOtherValues)[] = [
  "antifreeze",
  "dryIce",
  "handSanitizer",
  "refrigerant",
  "rockSalt",
];
